import React, { useState } from 'react';
import TextInput from './TextInput';
import useInputHelper from './inputHelper';
import { DefaultInputPropTypes } from './types';
import { buildPhone, getUnformattedPhoneNumber, splitPhone } from '../../utils/strings/phoneUtils';
import { useUiTheme } from 'framework/uiTheme';
import { FormFieldsData } from '../FormContext';

interface PhoneInputProps extends DefaultInputPropTypes<string> {
  hasExtension?: boolean;
}

export interface PhoneInputLayoutProps {
  phoneInput: React.ReactElement;
  extensionInput?: React.ReactElement;
  hasExtension?: boolean;
}

export type PhoneInputLayoutType = ReactComponent<PhoneInputLayoutProps>;

const PhoneInput = ({ hasExtension = false, ...props }: PhoneInputProps) => {
  const [isHasPlus, setPlus] = useState(false);
  const { PhoneInputLayout } = useUiTheme<PhoneInputLayoutType>();

  const inputHelper = useInputHelper(props);
  const { source } = props;

  const phoneObject = splitPhone(inputHelper.getValue()) || {
    phone: '',
    extension: '',
  };

  const phone = `${isHasPlus ? '+' : ''}${phoneObject && phoneObject.phone}`;
  const extension = phoneObject && phoneObject.extension;

  const phoneSource = `${source}`;
  const extensionSource = `${source}-ext`;

  /**
   * Handles callback of subinputs, merges it and call onChange event of the context or parent input.
   * @param eventValue
   */
  const onChangeCallback = (eventValue: FormFieldsData) => {
    const parentOnChangeCallback = inputHelper.getRawOnChangeCallback();

    if (eventValue[phoneSource] !== undefined) {
      setPlus(eventValue[phoneSource]?.includes('+'));
      phoneObject.phone = getUnformattedPhoneNumber(eventValue[phoneSource]);
    } else if (eventValue[extensionSource] !== undefined) {
      phoneObject.extension = eventValue[extensionSource];
    }

    if (parentOnChangeCallback) {
      parentOnChangeCallback({
        [source]: buildPhone(phoneObject),
      });
    }
  };

  const phoneInput = (
    <TextInput {...props} value={phone || ''} source={phoneSource} onChangeCallback={onChangeCallback} />
  );

  const extensionInput = (
    <TextInput
      {...props}
      value={extension || ''}
      source={extensionSource}
      onChangeCallback={onChangeCallback}
      label="Extension"
    />
  );

  return <PhoneInputLayout phoneInput={phoneInput} extensionInput={extensionInput} hasExtension={hasExtension} />;
};

export default PhoneInput;
